import { ForbiddenError, InvalidParamsError } from '@lessonup/domain-utils';
import { ExclusionType, orderableCompareFn, Pin, TeacherPin } from '@lessonup/pins-shared';
import { filter, pick, size, first, includes } from 'lodash';
import { AssignmentType } from '.';
import { PinUtils } from '../common';
import { Assignment } from './Assignment';
import { AssignmentContent } from './AssignmentContent';
import { AssignmentPin } from './AssignmentPin';
import { AssignmentSettings } from './AssignmentSettings';

/**
 * @deprecated slowly move this to other namespaces
 */

export interface AssignmentPinDict {
  [key: string]: AssignmentPin;
}

export class AssignmentUtils {
  public static filterGameMode(items: Assignment[] | undefined, type: AssignmentType): Assignment[] {
    return filter(items, (item) => AssignmentUtils.isMode(item, type));
  }

  public static isMode(assignment: Assignment | undefined, type: AssignmentType): boolean {
    return (assignment && assignment.settings && assignment.settings.type === type) || false;
  }

  public static pin(assignment: AssignmentContent | undefined, id: string | undefined): AssignmentPin | undefined {
    return (assignment && id && assignment.pins[id]) || undefined;
  }

  public static pinDictForIds(assignment: AssignmentContent | undefined, ids: string[]): AssignmentPinDict | undefined {
    return (assignment && ids && pick(assignment.pins, ids)) || undefined;
  }

  public static pins(assignment: AssignmentContent | undefined): AssignmentPin[] {
    if (!assignment || !assignment.pins) return [];
    return Object.values(assignment.pins).sort(orderableCompareFn);
  }

  public static interactivePins(assignment: Assignment | undefined): AssignmentPin[] {
    if (!assignment || !assignment.pins) return [];
    return Object.values(assignment.pins).filter((p) => PinUtils.isInteractive(p));
  }

  public static pinCount(assignment: Assignment | undefined): number | undefined {
    if (!assignment) return undefined;
    return size(assignment.pins);
  }

  public static unorderedPins(assignment: AssignmentContent | undefined): AssignmentPin[] {
    if (!assignment || !assignment.pins) return [];
    return Object.values(assignment.pins);
  }

  public static firstPin(assignment: AssignmentContent): AssignmentPin | undefined {
    return first(this.pins(assignment));
  }

  public static isVisibilityState(state: any): state is AssignmentSettings.Visibility {
    return ['all', 'studentOnly'].includes(state);
  }

  public static isVisibleToAll(assignment: Assignment): boolean {
    return assignment.visibilityState === 'all';
  }

  public static isVisibleToStudentsOnly(assignment: Assignment): boolean {
    return assignment.visibilityState === 'studentOnly';
  }

  public static isUserOwner(assignment: Assignment, userId: string) {
    return assignment.ownerId === userId;
  }

  /** throws an error when the assignment isn't joinable */
  public static validateJoinable(settings: AssignmentSettings, requiredType: [AssignmentType]): void {
    if (!requiredType.find((t) => t == settings.type)) {
      throw new ForbiddenError('assignment is not of correct type');
    }

    if (settings.status == 'closed') {
      throw new ForbiddenError('assignment is closed');
    }

    if (settings.type === 'realtime' && !settings.playerSettings.allowNewPlayers) {
      throw new ForbiddenError('assignment does not allow new students to join');
    }
  }

  private static isExcludedForExclusionType(pin: Pin, exclusion: ExclusionType): boolean {
    return pin.exclusions?.includes(exclusion) ?? false;
  }

  /** returns all student pins without exclusions based on the assignment type */
  public static pinsByAssignmentType(assignment: AssignmentContent): AssignmentPin[] {
    const exclusion: ExclusionType = assignment.type === 'realtime' ? 'realtime' : 'homework';
    return this.pinsExcluding(AssignmentUtils.pins(assignment), exclusion);
  }

  private static pinIsAssignmentType(pin: Pin, type: AssignmentType): boolean {
    const exclusion: ExclusionType = type === 'realtime' ? 'realtime' : 'homework';
    return !this.isExcludedForExclusionType(pin, exclusion);
  }

  /**
   * Returns the first pin that is supported and not excluded for the given assignment type
   */
  public static firstPinForAssignmentType<T extends Pin>(pins: T[], type: AssignmentType): T | undefined {
    const supportedPins = this.excludeUnsupportedData(pins);
    const pinsForAssignment = filter(supportedPins, (pin) => this.pinIsAssignmentType(pin, type));
    return first(pinsForAssignment);
  }

  public static pinsExcluding<T extends AssignmentPin>(pins: T[], exclusion: ExclusionType): T[] {
    return this.excludeUnsupportedData(pins).filter((pin) => !pin.exclusions || !includes(pin.exclusions, exclusion));
  }

  public static excludeUnsupportedData<T extends Pin>(pins: T[]): T[] {
    return pins
      .filter((pin) => pin.type !== 'UNSUPPORTED')
      .map((pin) => {
        if (!pin.pinComponents) return pin;

        return {
          ...pin,
          pinComponents: pin.pinComponents.filter((component) => component.type !== 'UNSUPPORTED'),
        };
      });
  }

  public static pinIsInAssignment(pin: AssignmentPin, assignment: Assignment): boolean {
    const type: ExclusionType = assignment.type === 'realtime' ? 'realtime' : 'homework';
    return !includes(pin.exclusions, type);
  }

  // TODO: type this
  public static getShuffledQuizAnswers(shuffledIndices: any, quizAnswers: any) {
    if (shuffledIndices.length != quizAnswers.length) {
      throw new InvalidParamsError('Arrays are not in sync');
    }

    return shuffledIndices.map((index: any) => quizAnswers[index]);
  }

  public static isV2Assignment(assignment: Assignment): boolean {
    return assignment.version === 'v2';
  }
}
